import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { getEffectiveRole } from "../constants/roles";

const BusinessUnitContext = createContext();

export const BusinessUnitProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  const [businessUnits, setBusinessUnits] = useState([]); // [{id, unit_name, unit_code, role, ...}]
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState(() => {
    return localStorage.getItem("selected_business_unit_id") || null;
  });
  const [isLoaded, setIsLoaded] = useState(false);
  // Profile from latest /auth/users/me (pilot_license_effective_access is BU-context-dependent)
  const [meProfile, setMeProfile] = useState(null);

  // Helper to set axios header when BU changes
  const applyHeader = useCallback((buId) => {
    if (buId) {
      axios.defaults.headers.common["X-Business-Unit-Id"] = buId;
    } else {
      delete axios.defaults.headers.common["X-Business-Unit-Id"];
    }
  }, []);

  // Persist + header update whenever selection changes
  useEffect(() => {
    if (selectedBusinessUnitId) {
      localStorage.setItem("selected_business_unit_id", selectedBusinessUnitId);
    } else {
      localStorage.removeItem("selected_business_unit_id");
    }
    applyHeader(selectedBusinessUnitId);
  }, [selectedBusinessUnitId, applyHeader]);

  // Fetch /auth/users/me on auth change and when BU changes (pilot_license_effective_access is BU-context-dependent)
  useEffect(() => {
    const fetchMe = async () => {
      if (!isAuthenticated) {
        setBusinessUnits([]);
        setSelectedBusinessUnitId(null);
        setMeProfile(null);
        setIsLoaded(true);
        return;
      }
      const url = `${process.env.REACT_APP_BASE_API_URL}/auth/users/me`;

      const applyResponse = (res) => {
        const data = res?.data || {};
        const buList = data.business_units || [];
        setBusinessUnits(buList);
        setMeProfile(data);
        // Auto-select first BU if none previously selected
        if (!selectedBusinessUnitId && buList.length > 0) {
          setSelectedBusinessUnitId(buList[0].id?.toString());
        }
        // Clear selection when user has no BU
        if (buList.length === 0) {
          setSelectedBusinessUnitId(null);
        }
        setIsLoaded(true);
      };

      try {
        const res = await axios.get(url);
        applyResponse(res);
      } catch (error) {
        // 403 often means stale X-Business-Unit-Id (user no longer has access to that BU)
        if (error?.response?.status === 403 && selectedBusinessUnitId) {
          delete axios.defaults.headers.common["X-Business-Unit-Id"];
          try {
            const retryRes = await axios.get(url);
            const data = retryRes?.data || {};
            const buList = data.business_units || [];
            setBusinessUnits(buList);
            setMeProfile(data);
            const selectedInList = buList.some((bu) => String(bu.id) === String(selectedBusinessUnitId));
            const newBuId = selectedInList
              ? selectedBusinessUnitId
              : (buList.length > 0 ? buList[0].id?.toString() : null);
            setSelectedBusinessUnitId(newBuId);
            if (newBuId) {
              axios.defaults.headers.common["X-Business-Unit-Id"] = newBuId;
            }
            setIsLoaded(true);
            return;
          } catch (retryErr) {
            console.error("Failed to fetch user profile / business units (retry)", retryErr);
            // Leave header deleted so other requests (e.g. PortalHeader) don't get 403
          }
        }
        console.error("Failed to fetch user profile / business units", error);
        setBusinessUnits([]);
        setMeProfile(null);
        setIsLoaded(true);
      }
    };

    fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, selectedBusinessUnitId]);

  const selectedBusinessUnit = businessUnits.find(
    (bu) => String(bu.id) === String(selectedBusinessUnitId)
  );

  const currentRole = selectedBusinessUnit?.role || null;
  const portalScope = meProfile?.portalScope || user?.portalScope || "internal";
  const consultantId = meProfile?.consultantId ?? user?.consultantId ?? null;
  const consultantAccessStatus =
    meProfile?.consultantAccessStatus ?? user?.consultantAccessStatus ?? null;
  const isConsultantPortal = portalScope === "consultant";
  
  // Effective role: BU role takes precedence over base user role
  // This is the role that should be used for permissions
  const effectiveRole = getEffectiveRole(user, selectedBusinessUnit);

  // Pilot access: use pilot_license_effective_access from /auth/users/me (BU-context-dependent).
  // Fallback for older backends: derive from BU/user fields; default false when unknown.
  const pilotLicenseEffectiveAccess =
    meProfile?.pilot_license_effective_access ??
    (selectedBusinessUnit
      ? (selectedBusinessUnit?.has_pilot_license_for_user ?? selectedBusinessUnit?.pilot_license_enabled)
      : (user?.has_pilot_license ?? user?.pilot_license_enabled));
  const hasPilotLicense = Boolean(pilotLicenseEffectiveAccess);

  // Feature flags derived from the selected BU.
  // When no BU is selected (transient state or user with no BUs), default to all
  // features OFF to avoid exposing modules the user should not see.
  const features = isConsultantPortal
    ? {
        has_profile: false,
        has_cv: false,
        has_skills: false,
        has_pilot: false,
        has_pilot_license: false,
        has_partners: false,
        has_consultant_portal: true,
      }
    : selectedBusinessUnit
    ? {
        has_profile: Boolean(selectedBusinessUnit?.has_profile),
        has_cv: Boolean(selectedBusinessUnit?.has_cv),
        has_skills: Boolean(selectedBusinessUnit?.has_skills),
        has_pilot: Boolean(selectedBusinessUnit?.has_pilot),
        has_pilot_license: Boolean(selectedBusinessUnit?.has_pilot) && hasPilotLicense,
        has_partners: Boolean(selectedBusinessUnit?.has_partners),
        has_consultant_portal: false,
      }
    : {
        has_profile: false,
        has_cv: false,
        has_skills: false,
        has_pilot: false,
        has_pilot_license: false,
        has_partners: false,
        has_consultant_portal: false,
      };

  const value = {
    businessUnits,
    selectedBusinessUnitId,
    selectedBusinessUnit,
    setSelectedBusinessUnitId,
    currentRole,
    effectiveRole,
    features,
    isLoaded,
    meProfile,
    portalScope,
    consultantId,
    consultantAccessStatus,
    isConsultantPortal,
    landingPath: isConsultantPortal
      ? "/portal/consultant/dashboard"
      : "/portal/dashboard",
  };

  return (
    <BusinessUnitContext.Provider value={value}>{children}</BusinessUnitContext.Provider>
  );
};

export const useBusinessUnit = () => useContext(BusinessUnitContext);
